import { useContext, useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { format, formatDistanceStrict, isValid } from 'date-fns';

import { Box, Heading, Paragraph, Popover, Text, TextButton, TriggerText } from '@zillow/constellation-10';
import { LoadingParagraph } from '@zg-rentals/loading-components';

import { useSelector } from '../../../../selectors/useSelector';

import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../../../hooks/queries/useGetPropertyDetailsOverview';

import { useGetListingSummary } from '../../../../hooks/queries/summary';
import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';
import { updateLoadingState } from '../../../../providers/LoadingMapContext/actions';

import numberUtils from '../../../../utils/numberUtils';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import SimpleCard from '../../../SimpleCard';

import { containerStyles, detailContainerStyles, verticalLineStyles } from './styles';

const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/' as const;
const activeForLink =
  'https://www.zillow.help/article/how-is-the-days-listed-counter-determined-zd30237392565907' as const;

type Props = {
  displayType?: VERTICAL_DISPLAY_TYPE;
  rentZestimate?: number;
};

const Details = ({ rentZestimate = 0, displayType = VERTICAL_DISPLAY_TYPE.FULL }: Props) => {
  const dispatch = useDispatch();
  const {
    loadingByComponent: { ListingModule: isListingModuleLoading, ConnectedListing: isConnectedListingLoading },
    reducerDispatch,
  } = useContext(LoadingMapContext);

  const { currentProperty, listingAlias } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const { data: listingSummaryData = {}, isLoading: isListingSummaryLoading } = useGetListingSummary(listingAlias);

  const { data: propertyDetailsOverviewData, isLoading: isPropertyDetailsOverviewLoading } =
    useGetPropertyDetailsOverview(listingAlias);

  const {
    // @ts-expect-error
    dateActivatedTimestamp: dateLastActivatedTimestamp,
    // @ts-expect-error
    syndication: { overview: { displayStatus = '' } = {} } = {},
  } = listingSummaryData;

  const status = displayStatus.toLowerCase();
  const lastListed = isValid(new Date(Number(dateLastActivatedTimestamp)))
    ? format(new Date(Number(dateLastActivatedTimestamp)), 'MMM yyyy')
    : '-';
  const listedFor =
    dateLastActivatedTimestamp && status !== LISTING_SUMMARY_DISPLAY_STATUS.DECLINED
      ? formatDistanceStrict(new Date(), new Date(Number(dateLastActivatedTimestamp)))
      : '-';
  const isPageLoading = displayType === VERTICAL_DISPLAY_TYPE.FULL ? isConnectedListingLoading : isListingModuleLoading;

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        listingSummary: isListingSummaryLoading,
        propertyDetailsOverview: isPropertyDetailsOverviewLoading,
      },
    });
  }, [isListingSummaryLoading]);

  const renderDetails = () => (
    <Box css={detailContainerStyles}>
      <LoadingParagraph
        textStyle="body-sm"
        isLoading={isPageLoading}
        loadingWidth="40%"
        css={{ marginBottom: '100', whiteSpace: 'nowrap' }}
      >
        Active for{' '}
        <Popover
          header={<Heading level={6}>Calculating your active time</Heading>}
          body={
            <>
              <Paragraph css={{ marginBottom: '400' }}>
                This counter shows the amount of time since you last activated your listing. If you deactivate and then
                reactivate within 31 days, your public listing will show the cumulative time it has been active.
              </Paragraph>
              <Popover.Close>
                <TextButton asChild>
                  <a target="_blank" href={activeForLink} rel="noreferrer">
                    Learn more
                  </a>
                </TextButton>
              </Popover.Close>
            </>
          }
        />
      </LoadingParagraph>
      <LoadingParagraph
        textStyle="body-bold"
        isLoading={isPageLoading}
        loadingWidth="100%"
        css={{
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {listedFor}
      </LoadingParagraph>
    </Box>
  );

  const renderInactiveDetails = () => (
    <>
      <Box css={detailContainerStyles}>
        <Text
          textStyle="body-sm"
          asChild
          css={{
            marginBottom: '100',
            color: 'text.neutral',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          <p>Last listed</p>
        </Text>
        <Text
          textStyle="body-bold"
          asChild
          css={{
            color: 'text.neutral',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          <p>{lastListed || '-'}</p>
        </Text>
      </Box>
      {rentZestimate !== 0 && (
        <>
          <Box css={verticalLineStyles} data-testid="StyledVerticalLine" />
          <Box css={detailContainerStyles}>
            <Text
              textStyle="body-sm"
              asChild
              css={{
                marginBottom: '100',
                color: 'text.subtle',
                whiteSpace: { base: 'normal', md: 'nowrap' },
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              <p>Rent Zestimate®</p>
            </Text>
            <Text
              textStyle="body-bold"
              asChild
              css={{
                color: 'text.subtle',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              <p>
                <Popover
                  header={<Heading level={6}>Rent Zestimate®</Heading>}
                  body={
                    <>
                      The Rent Zestimate is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
                      <TextButton asChild textStyle="body">
                        <a target="_blank" href={rentZestimateLink} rel="noreferrer">
                          Learn more
                        </a>
                      </TextButton>
                      {' about the Rent Zestimate.'}
                    </>
                  }
                  trigger={<TriggerText>{numberUtils.asCurrency(rentZestimate)}</TriggerText>}
                />
              </p>
            </Text>
          </Box>
        </>
      )}
    </>
  );

  const renderDetailsComponentContent = () => {
    return <>{status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE ? renderInactiveDetails() : renderDetails()}</>;
  };

  return (
    <>
      {displayType === VERTICAL_DISPLAY_TYPE.FULL ? (
        <Box css={containerStyles} data-testid="Details-Full">
          {renderDetailsComponentContent()}
        </Box>
      ) : (
        <SimpleCard>
          <Box css={{ ...containerStyles, marginBottom: '0' }} data-testid="Details-Simple">
            {renderDetailsComponentContent()}
          </Box>
        </SimpleCard>
      )}
    </>
  );
};

export default Details;
